import type { FormEvent, RefObject } from "react";
import { Check, ExternalLink, Loader2, Search, SkipForward, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { RichTextRenderer } from "@/components/ui/RichTextRenderer";
import type { RecentComment } from "@/api/enrichment";

export type EnrichmentCardMode = "enrichment" | "correction";

/** Name inputs differ by mode: classic enrichment splits first/last and
 *  rejoins on save, corrections edit the single `full_name` the backend
 *  actually stores. Modelled as a union so a caller can't pass one
 *  mode's fields with the other's mode. */
type NameFields =
  | {
      mode: "enrichment";
      firstName: string;
      lastName: string;
      onFirstName: (v: string) => void;
      onLastName: (v: string) => void;
    }
  | {
      mode: "correction";
      fullName: string;
      onFullName: (v: string) => void;
    };

type EnrichmentCardProps = NameFields & {
  /** Header */
  companyName: string;
  taxId?: string | null;
  city?: string | null;
  postalCode?: string | null;
  /** "Dirigeant: X · N ans" strip — enrichment mode only. */
  leaderName?: string | null;
  leaderAge?: number | null;
  leaderTenure?: number | null;
  /** Sales notes explaining what was reported wrong — correction mode. */
  recentComments?: RecentComment[];
  /** Company notes, shown read-only so the contractor gets the sales
   *  context without being able to overwrite it. */
  companyNotes?: string | null;
  /** Shared form fields */
  title: string;
  onTitle: (v: string) => void;
  linkedin: string;
  onLinkedin: (v: string) => void;
  phone: string;
  onPhone: (v: string) => void;
  email: string;
  onEmail: (v: string) => void;
  /** Pre-encoded Google query for the "Search LinkedIn" affordance. */
  linkedinSearchQuery: string;
  autoFocusRef?: RefObject<HTMLInputElement>;
  /** Footer */
  isSaving: boolean;
  canSubmit: boolean;
  onSubmit: (e?: FormEvent) => void;
  onSkip: () => void;
  onCantFind: () => void;
};

const LABEL = "text-[10px] font-medium uppercase text-slate-400";

/** The enrichment card — company header, contact form and the
 *  Skip / Can't find / Save & Next footer. Presentational: every
 *  handler and all validation live in the container, so the classic
 *  Work flow keeps its exact payloads. */
export function EnrichmentCard(props: EnrichmentCardProps) {
  const {
    mode,
    companyName,
    taxId,
    city,
    postalCode,
    leaderName,
    leaderAge,
    leaderTenure,
    recentComments,
    companyNotes,
    title,
    onTitle,
    linkedin,
    onLinkedin,
    phone,
    onPhone,
    email,
    onEmail,
    linkedinSearchQuery,
    autoFocusRef,
    isSaving,
    canSubmit,
    onSubmit,
    onSkip,
    onCantFind,
  } = props;

  const hasPhone = !!phone.trim();
  const hasEmail = !!email.trim();
  const isCorrection = mode === "correction";

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-5 rounded-plane bg-white p-6 shadow-plane"
    >
      {isCorrection && (
        <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
          <div className="flex items-start gap-2 text-sm font-medium text-amber-800">
            <span aria-hidden>⚠</span>
            <span>
              Flagged as wrong data, verify owner identity and phone number
            </span>
          </div>
          {(recentComments ?? []).length > 0 && (
            <ul className="space-y-1.5 border-t border-amber-200/70 pt-2">
              {(recentComments ?? []).map((c, i) => (
                <li key={i} className="text-xs text-amber-900/80">
                  <span className="font-medium uppercase text-amber-700">
                    {c.source}
                  </span>
                  {c.author && <span> · {c.author}</span>}
                  {c.created_at && (
                    <span className="text-amber-700/60">
                      {" "}
                      · {new Date(c.created_at).toLocaleDateString("fr")}
                    </span>
                  )}
                  <div className="whitespace-pre-wrap text-amber-900">
                    {c.text}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-900">{companyName}</h2>
          <div className="mt-1 flex items-center gap-2 text-sm text-slate-500">
            {taxId && <span className="font-mono">{taxId}</span>}
            {taxId && (city || postalCode) && <span>·</span>}
            {(city || postalCode) && (
              <span>
                {city} {postalCode}
              </span>
            )}
          </div>
        </div>
        {taxId && (
          <a
            href={`https://www.pappers.fr/entreprise/${taxId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-600 transition-all hover:bg-indigo-100"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Pappers
          </a>
        )}
      </div>

      {!isCorrection && (leaderName || leaderAge != null) && (
        <div className="text-sm text-slate-600">
          Dirigeant: <strong>{leaderName ?? "—"}</strong>
          {leaderAge != null && <> · {leaderAge} ans</>}
          {leaderTenure != null && <> · {leaderTenure} ans tenure</>}
        </div>
      )}

      {isCorrection && companyNotes && companyNotes.trim() && (
        <div className="rounded-xl border border-slate-900/[0.06] bg-slate-50/60 p-3">
          <div className="mb-1 text-[10px] font-medium uppercase text-slate-400">
            Company notes
          </div>
          <RichTextRenderer
            content={companyNotes}
            className="text-sm text-slate-600"
          />
        </div>
      )}

      <div className="space-y-3">
        {props.mode === "enrichment" ? (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={LABEL}>First name</label>
              <Input
                ref={autoFocusRef}
                value={props.firstName}
                onChange={(e) => props.onFirstName(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div>
              <label className={LABEL}>Last name</label>
              <Input
                value={props.lastName}
                onChange={(e) => props.onLastName(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>
        ) : (
          <div>
            <label className={LABEL}>Full name</label>
            <Input
              ref={autoFocusRef}
              value={props.fullName}
              onChange={(e) => props.onFullName(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
        )}

        <div>
          <label className={LABEL}>Title / Position</label>
          <Input
            value={title}
            onChange={(e) => onTitle(e.target.value)}
            className="h-9 text-sm"
            placeholder="Gérant, Président, DG..."
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className={LABEL}>LinkedIn</label>
            <a
              href={`https://www.google.com/search?q=${linkedinSearchQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-0.5 text-[10px] text-indigo-500 hover:text-indigo-700"
            >
              <Search className="h-3 w-3" /> Search LinkedIn ↗
            </a>
          </div>
          <Input
            value={linkedin}
            onChange={(e) => onLinkedin(e.target.value)}
            className="h-9 font-mono text-sm"
            placeholder="linkedin.com/in/..."
          />
          {linkedin && (
            <div className="mt-1.5 flex items-center gap-2">
              <a
                href={`https://app.kaspr.io/search?linkedin=${encodeURIComponent(linkedin)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-blue-50 px-2 py-1 text-[10px] font-medium text-blue-600 hover:bg-blue-100"
              >
                🔍 Kaspr
              </a>
              <a
                href={`https://app.lusha.com/search?linkedin=${encodeURIComponent(linkedin)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-green-50 px-2 py-1 text-[10px] font-medium text-green-600 hover:bg-green-100"
              >
                🔍 Lusha
              </a>
            </div>
          )}
        </div>

        <div>
          <label className={LABEL}>
            Phone
            {isCorrection && <span className="ml-1 text-rose-500">*</span>}
          </label>
          <Input
            value={phone}
            onChange={(e) => onPhone(e.target.value)}
            className="h-9 font-mono text-sm"
            placeholder="+33 6 ..."
          />
        </div>

        <div>
          <label className={LABEL}>Email</label>
          <Input
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            className="h-9 font-mono text-sm"
            placeholder="email@company.com"
          />
        </div>

        <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          {isCorrection ? (
            hasPhone ? (
              <span className="text-emerald-600">
                ✓ Has phone → <strong>To Be Contacted</strong>
              </span>
            ) : (
              <span className="text-slate-400">
                Phone required to close this correction, or use
                &ldquo;Can&rsquo;t find&rdquo;
              </span>
            )
          ) : (
            <>
              {hasPhone && hasEmail && (
                <span className="text-emerald-600">
                  ✓ Has phone + email → <strong>To Be Contacted</strong>
                </span>
              )}
              {hasPhone && !hasEmail && (
                <span className="text-amber-600">
                  ✓ Has phone only → <strong>Email Pending</strong> + tag
                  &ldquo;Email missing&rdquo;
                </span>
              )}
              {!hasPhone && hasEmail && (
                <span className="text-indigo-600">
                  ✓ Has email only → <strong>Email Pending</strong> + tag
                  &ldquo;Phone missing&rdquo;
                </span>
              )}
              {!hasPhone && !hasEmail && (
                <span className="text-slate-400">
                  No phone or email yet, fill in or skip
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-slate-900/[0.06] pt-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <SkipForward className="h-4 w-4" /> Skip
            <kbd className="ml-1 rounded bg-slate-100 px-1 text-[9px] text-slate-400">
              S
            </kbd>
          </button>
          <button
            type="button"
            onClick={onCantFind}
            className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-rose-500 hover:bg-rose-50 hover:text-rose-700"
          >
            <X className="h-4 w-4" /> Can&rsquo;t find
            <kbd className="ml-1 rounded bg-rose-50 px-1 text-[9px] text-rose-400">
              X
            </kbd>
          </button>
        </div>

        <Button
          type="submit"
          disabled={isSaving || !canSubmit}
          size="lg"
          className={cn(!canSubmit && "cursor-not-allowed")}
        >
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-2 h-4 w-4" />
          )}
          Save &amp; Next
          <kbd className="ml-2 text-[10px] opacity-60">Enter</kbd>
        </Button>
      </div>
    </form>
  );
}
